import type { EngineBlastRadius } from "@/services/migrations/engine";

/**
 * What else automates the object this task migrates.
 *
 * ADVISORY, ALWAYS. It is never an input to a compiler and never folded into a coverage
 * number: a figure that moved because a graph was stale would mean a stale graph had
 * graded a migration. It is here because a reviewer deciding whether to add a Flow to an
 * object needs to know what is already running on it.
 *
 * THE ARMS ARE REPORTED WITH THE RESULT, and that pairing is the whole value of the
 * section. An empty blast radius from three failed arms is a completely different claim
 * from an empty one from three successful arms, and only the second means "nothing else
 * touches this object". Rendering the list without the arms would present the first as
 * the second.
 */
export function EngineBlastRadiusPanel({ blastRadius }: { blastRadius: EngineBlastRadius }) {
  const { entries, resolved, unavailable, object, fields } = blastRadius;
  const blind = unavailable.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
        {object ? <span className="text-foreground">{object}</span> : null}
        <span>{resolved.length} arm(s) answered</span>
        {blind ? (
          <span className="text-[hsl(var(--accent-warm))]">
            {unavailable.length} did not ({unavailable.join(", ")})
          </span>
        ) : null}
      </div>

      {entries.length === 0 ? (
        <p className="max-w-prose text-xs text-muted-foreground">
          {blind
            ? "Nothing was found — but some arms did not answer, so this is not the same as “nothing else touches this object”."
            : "Every arm answered and found nothing else automating this object."}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {entries.map((entry, index) => (
            <li key={`${entry.kind}-${entry.name}-${index}`} className="space-y-0.5 py-1.5">
              <div className="flex flex-wrap items-baseline gap-x-2 font-mono text-[11px]">
                <span className="text-muted-foreground">{entry.kind}</span>
                <span className="break-all text-foreground">{entry.name}</span>
                {/* Tri-state: an automation whose active flag could not be read is not
                    inactive, and is not reported as such. */}
                <span
                  className={
                    entry.active === false ? "text-muted-foreground/70" : "text-[hsl(var(--accent-warm))]"
                  }
                >
                  {entry.active == null ? "active unknown" : entry.active ? "active" : "inactive"}
                </span>
                {entry.stale_risk ? <span className="text-destructive">may be stale</span> : null}
              </div>
              {entry.detail ? (
                <p className="max-w-prose text-[11px] text-muted-foreground">{entry.detail}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {fields.length > 0 ? (
        <p className="break-words font-mono text-[11px] text-muted-foreground">
          fields: {fields.join(", ")}
        </p>
      ) : null}
    </div>
  );
}
